import { api } from "../lib";
import {
  isLegacyTechnicalReportError,
  LEGACY_TECHNICAL_REPORT_MESSAGE,
} from "../utils/legacyTechnicalReportError";
import type { LegacyHistoricalReportHeader } from "../types/legacyHistoricalReport";
import type { DocumentViewResponse } from "../types/reportTemplate";
import type {
  DirectCorrectionAmendmentResponse,
  DirectCorrectionPreviewResponse,
} from "./patientInfoService";
import {
  ReportContentChangedError,
  type ReportContentVersion,
  type ReportPreviewEvidence,
} from "./signOff.service";

export type LegacyCorrectableHeader = Omit<
  LegacyHistoricalReportHeader,
  "caseNumber" | "reportedDateText"
>;
export interface LegacyReportCorrectionBody {
  reason: string;
  legacyReportCorrection: { header: Partial<LegacyCorrectableHeader> };
  correctionId?: string;
  correctionTimestamp?: string;
  previewEvidence?: ReportPreviewEvidence;
}

async function request<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (isLegacyTechnicalReportError(error))
      throw new Error(LEGACY_TECHNICAL_REPORT_MESSAGE);
    const response = (
      error as { response?: { status?: number; data?: { code?: string } } }
    )?.response;
    if (
      response?.status === 409 &&
      response.data?.code === "REPORT_CONTENT_CHANGED"
    )
      throw new ReportContentChangedError();
    throw new Error(
      response?.status === 403
        ? "You do not have permission to sign this historical report correction."
        : response?.status === 409
        ? "The report changed while you were editing. Your correction is retained. Review the updated report before signing."
        : response?.status === 400 || response?.status === 422
        ? "Check the correction fields and reason. Only historical report identity fields may be corrected here."
        : "The historical report correction could not be completed. Your edits are retained. Check your connection and try again."
    );
  }
}

export const legacyReportCorrectionService = {
  get: (caseId: string) =>
    request(() =>
      api.get<
        DocumentViewResponse & { legacyReportHeader?: LegacyCorrectableHeader }
      >(`/cases/${caseId}/document-view`)
    ),
  preview: (caseId: string, body: LegacyReportCorrectionBody) =>
    request(() =>
      api.post<DirectCorrectionPreviewResponse>(
        `/cases/${caseId}/direct-correction/preview`,
        body
      )
    ),
  acknowledge: (
    caseId: string,
    body: LegacyReportCorrectionBody,
    contentVersion: ReportContentVersion
  ) =>
    request(async () => {
      const response = await api.post<{
        previewEvidence: ReportPreviewEvidence;
      }>(`/cases/${caseId}/direct-correction/preview-review`, {
        ...body,
        contentVersion,
      });
      return response.previewEvidence;
    }),
  apply: (caseId: string, body: LegacyReportCorrectionBody) =>
    request(() =>
      api.post<DirectCorrectionAmendmentResponse>(
        `/cases/${caseId}/direct-correction`,
        body
      )
    ),
};
